/* eslint-disable no-restricted-imports */
/* eslint-disable import/no-relative-packages */
// Resource files
import type app from '../../../locales/en-US/app.json';
import type common from '../../../locales/en-US/common.json';
import type designOrders from '../../../locales/en-US/designOrders.json';
import type designStudio from '../../../locales/en-US/designStudio.json';
import type ehi from '../../../locales/en-US/ehi.json';
import type homeownerApp from '../../../locales/en-US/homeownerApp.json';
import type login from '../../../locales/en-US/login.json';
import type manageTeams from '../../../locales/en-US/manageTeams.json';
import type newConstruction from '../../../locales/en-US/newConstruction.json';
import type notFound from '../../../locales/en-US/notFound.json';
import type paintMeasurements from '../../../locales/en-US/paintMeasurements.json';
import type properties from '../../../locales/en-US/properties.json';
import type propertyDetails from '../../../locales/en-US/propertyDetails.json';
import type shared from '../../../locales/en-US/shared.json';
import type signUp from '../../../locales/en-US/signUp.json';
import type somethingWrong from '../../../locales/en-US/somethingWrong.json';
import type virtualWalkthrough from '../../../locales/en-US/virtualWalkthrough.json';
// End Resource files

export const TranslationNamespace = {
  APP: 'app',
  COMMON: 'common',
  DESIGN_ORDERS: 'designOrders',
  DESIGN_STUDIO: 'designStudio',
  EHI: 'ehi',
  HOMEOWNER_APP: 'homeownerApp',
  PROPERTY_DETAILS: 'propertyDetails',
  LOGIN: 'login',
  NOT_FOUND: 'notFound',
  PROPERTIES: 'properties',
  SHARED: 'shared',
  SIGN_UP: 'signUp',
  NEW_CONSTRUCTION: 'newConstruction',
  PAINT_MEASUREMENTS: 'paintMeasurements',
  SOMETHING_WRONG: 'somethingWrong',
  VIRTUAL_WALKTHROUGH: 'virtualWalkthrough',
  MANAGE_TEAMS: 'manageTeams',
} as const;

export const fallbackNS = [
  TranslationNamespace.APP,
  TranslationNamespace.COMMON,
] as const;

export const SupportedTranslationNamespace =
  Object.values(TranslationNamespace);

export type Resources = {
  [TranslationNamespace.APP]: typeof app;
  [TranslationNamespace.COMMON]: typeof common;
  [TranslationNamespace.DESIGN_ORDERS]: typeof designOrders;
  [TranslationNamespace.DESIGN_STUDIO]: typeof designStudio;
  [TranslationNamespace.EHI]: typeof ehi;
  [TranslationNamespace.HOMEOWNER_APP]: typeof homeownerApp;
  [TranslationNamespace.PROPERTY_DETAILS]: typeof propertyDetails;
  [TranslationNamespace.LOGIN]: typeof login;
  [TranslationNamespace.NEW_CONSTRUCTION]: typeof newConstruction;
  [TranslationNamespace.PAINT_MEASUREMENTS]: typeof paintMeasurements;
  [TranslationNamespace.PROPERTIES]: typeof properties;
  [TranslationNamespace.NOT_FOUND]: typeof notFound;
  [TranslationNamespace.SHARED]: typeof shared;
  [TranslationNamespace.SIGN_UP]: typeof signUp;
  [TranslationNamespace.SOMETHING_WRONG]: typeof somethingWrong;
  [TranslationNamespace.VIRTUAL_WALKTHROUGH]: typeof virtualWalkthrough;
  [TranslationNamespace.MANAGE_TEAMS]: typeof manageTeams;
};

export type TranslationNamespaceType =
  (typeof TranslationNamespace)[keyof typeof TranslationNamespace];
// Creates a type that includes the fallback namespaces since i18next
// accepts keys which are not apart of the namespace passed to the `t`
// function.
export type WithFallbackNS<T> = T extends TranslationNamespaceType
  ? T | FallbackNS
  : T extends TranslationNamespaceType[]
  ? T[number] | FallbackNS
  : never;

export type FallbackNS = (typeof fallbackNS)[number];

type PluralSuffix = 'zero' | 'one' | 'two' | 'few' | 'many' | 'other';
type WithOrWithoutPlural<K> = K extends `${infer B}_${PluralSuffix}` ? B : K;

type PathImpl<T, Key extends keyof T> = Key extends string
  ? T[Key] extends Record<string, unknown>
    ?
        | `${Key}.${PathImpl<T[Key], Exclude<keyof T[Key], keyof unknown[]>> &
            string}`
        | `${WithOrWithoutPlural<Key>}`
    : `${WithOrWithoutPlural<Key>}`
  : never;

type Path<T> = PathImpl<T, keyof T> | WithOrWithoutPlural<keyof T>;
type PathsForUnion<T> = T extends infer U
  ? U extends keyof Resources
    ? Path<Resources[U]>
    : never
  : never;

// Adds fallback translations in addition to the passed namespaces
export type Translations<T extends keyof Resources = keyof Resources> =
  PathsForUnion<T | FallbackNS>;

export type Variables = {
  [key: string]: string | number | React.ReactNode;
  count?: number;
};
